import { useId, useState, type ReactNode } from 'react';

import { useLanguage } from '../../i18n/language.js';
import { contextSourceFrom } from '../../lib/classifyContextUrl.js';
import { createMerchantIntake, processMerchantIntake } from '../../merchant/api.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { rememberIntakeId } from '../../merchant/data.js';
import { Button, Field, Modal, useToast } from '../../merchant/ui/primitives.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';

/**
 * The wireframe's "New Production Line" modal, on the real intake contract:
 * a website (required), a prompt (optional) and one Instagram or TikTok
 * profile (optional enrichment). The name is the server's: it names the
 * line after the business it reads, so the field is shown but not asked.
 *
 * Submitting creates the intake and starts its processing; the workspace
 * then re-reads the Production Lines the server owns and opens the new one.
 */
export function NewProductionLineModal({
  onClose,
  onCreated,
  initialUrl = '',
  initialPrompt = '',
  editing = false,
}: {
  readonly onClose: () => void;
  readonly onCreated: (intakeId: string, lineId: string | null) => void;
  readonly initialUrl?: string;
  readonly initialPrompt?: string;
  /** The wireframe's "Edit brief": same form, framed as a fresh start. */
  readonly editing?: boolean;
}): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const workspace = useWorkspace();
  const { businessName } = useMerchantWorkspaceIdentity();
  const toast = useToast();
  const ids = { url: useId(), prompt: useId(), social: useId(), name: useId() };
  const [url, setUrl] = useState(initialUrl);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [social, setSocial] = useState('');
  const [errors, setErrors] = useState<{
    url?: string;
    social?: string;
    submit?: string;
  }>({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const website = contextSourceFrom(url.trim(), 'website');
    const socialValue = social.trim();
    const socialSource =
      socialValue.length === 0
        ? null
        : (contextSourceFrom(socialValue, 'instagram') ??
          contextSourceFrom(socialValue, 'tiktok'));
    const next: { url?: string; social?: string } = {};
    if (website === null) next.url = copy.newLine.urlError;
    if (socialValue.length > 0 && socialSource === null)
      next.social = copy.newLine.socialError;
    if (next.url !== undefined || next.social !== undefined) {
      setErrors(next);
      window.setTimeout(() => {
        document
          .querySelector<HTMLElement>('.mj-ws-modal [aria-invalid="true"]')
          ?.focus();
      }, 30);
      return;
    }
    if (website === null) return;
    setBusy(true);
    setErrors({});
    try {
      const created = await createMerchantIntake({
        contextNote: prompt.trim(),
        // The interface language at submission is the analysis language.
        outputLocale: language,
        sources: [
          { url: website.url, platform: 'website', ownership: 'mine' },
          ...(socialSource === null
            ? []
            : [
                {
                  url: socialSource.url,
                  platform: socialSource.platform,
                  ownership: 'mine' as const,
                },
              ]),
        ],
      });
      rememberIntakeId(created.id);
      // Processing is server work; it continues whether or not this awaits it.
      void processMerchantIntake(created.id).catch(() => undefined);
      const fresh = await workspace.refresh();
      const line =
        fresh?.pending.find(
          (item) => (item.intakeId ?? item.brief?.intakeId) === created.id,
        ) ?? null;
      onCreated(created.id, line?.productionLineId ?? null);
      toast(copy.newLine.created(businessName?.trim() || copy.nav.yourBusiness));
    } catch {
      setErrors({ submit: copy.newLine.failed });
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? copy.newLine.editTitle : copy.newLine.title}
      description={editing ? copy.newLine.briefStartsNew : copy.newLine.body}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {copy.common.cancel}
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy
              ? copy.newLine.submitting
              : editing
                ? copy.newLine.saveBrief
                : copy.newLine.submit}
          </Button>
        </>
      }
    >
      <Field label={copy.newLine.url} id={ids.url} error={errors.url ?? null}>
        <input
          id={ids.url}
          className="mj-ws-inp"
          dir="ltr"
          data-autofocus="1"
          value={url}
          placeholder={copy.newLine.urlPlaceholder}
          aria-invalid={errors.url !== undefined}
          aria-describedby={errors.url !== undefined ? `${ids.url}-err` : undefined}
          onChange={(event) => {
            setUrl(event.target.value);
            setErrors(({ url: _url, ...rest }) => rest);
          }}
        />
      </Field>
      <Field
        label={copy.newLine.prompt}
        id={ids.prompt}
        optional={copy.newLine.optional}
      >
        <textarea
          id={ids.prompt}
          className="mj-ws-inp"
          rows={3}
          value={prompt}
          placeholder={copy.newLine.promptPlaceholder}
          onChange={(event) => {
            setPrompt(event.target.value);
          }}
        />
      </Field>
      <Field
        label={copy.newLine.social}
        id={ids.social}
        optional={copy.newLine.optional}
        hint={copy.newLine.socialHint}
        error={errors.social ?? null}
      >
        <input
          id={ids.social}
          className="mj-ws-inp"
          dir="ltr"
          value={social}
          placeholder={copy.newLine.socialPlaceholder}
          aria-invalid={errors.social !== undefined}
          aria-describedby={
            errors.social !== undefined ? `${ids.social}-err` : `${ids.social}-hint`
          }
          onChange={(event) => {
            setSocial(event.target.value);
            setErrors(({ social: _social, ...rest }) => rest);
          }}
        />
      </Field>
      <Field
        label={copy.newLine.name}
        id={ids.name}
        optional={copy.newLine.optional}
        hint={copy.newLine.nameHint}
      >
        <input
          id={ids.name}
          className="mj-ws-inp"
          value=""
          disabled
          readOnly
          aria-describedby={`${ids.name}-hint`}
          placeholder={businessName ?? ''}
        />
      </Field>
      {errors.submit !== undefined ? (
        <div className="mj-ws-err mj-ws-mt-16" role="alert">
          {errors.submit}
        </div>
      ) : null}
    </Modal>
  );
}
